import fp from 'lodash/fp';
import { push } from 'react-router-redux';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import { routes } from '../../../../constants';
import { TaskStatus } from '../../../../constants/tasks';
import { apiActionResult } from '../../common';
import { authTokenSelector } from '../../session';
import { apiActions as userApiActions, default as userActions } from '../../user/actions';
import { getApiUser } from '../../user/configurations/get';
import { currentUserSelector } from '../../user/selectors';
import { actions, apiActions } from './actions';

export function* subscribePlan(action) {
  const authToken = yield select(authTokenSelector);

  const { businessId } = action.payload.sourceToken;

  if (businessId) {
    yield put(apiActions.business.subscribePlan(authToken, businessId, action.payload.sourceToken));
  }

  const subscribeResult = yield apiActionResult(actions.business.api.subscribePlan);

  if (subscribeResult.success) {
    yield put(userApiActions.user.get(authToken));

    const userResult = yield apiActionResult(userActions.user.api.get);

    if (userResult.success) {
      yield put(push({ pathname: action.payload.nextUrl }));
    }
  }
}

export function* addAfsl(action) {
  const authToken = yield select(authTokenSelector);

  const { afsLicence, nextUrl } = action.payload;

  yield put(apiActions.business.addAfsLicence(authToken, afsLicence.businessId, afsLicence));

  const afsLicenceResult = yield apiActionResult(actions.business.api.addAfsLicence);

  if (afsLicenceResult.success && !afsLicence.noRedirect) {
    yield put(push({ pathname: nextUrl }));
  }
}

export function* removeBusinessManager(action) {
  const authToken = yield select(authTokenSelector);
  yield put(apiActions.business.removeBusinessManager(authToken, action.payload.businessId, action.payload.memberData));
  const removeBusinessManagerResult = yield apiActionResult(actions.business.api.removeBusinessManager);
  if (removeBusinessManagerResult.success) {
    yield put(apiActions.business.teamMembers(authToken, action.payload.businessId));
    yield getApiUser(authToken);
  }
}

export function* addBusinessManager(action) {
  const authToken = yield select(authTokenSelector);
  yield put(apiActions.business.addBusinessManager(authToken, action.payload.businessId, action.payload.memberData));
  const addBusinessManagerResult = yield apiActionResult(actions.business.api.addBusinessManager);
  if (addBusinessManagerResult.success) {
    yield put(apiActions.business.teamMembers(authToken, action.payload.businessId));
    yield getApiUser(authToken);
  }
}

export function* removeBusinessMember(action) {
  const authToken = yield select(authTokenSelector);
  yield put(apiActions.business.removeMember(authToken, action.payload.businessId, action.payload.memberData));
  const removeMemberResult = yield apiActionResult(actions.business.api.removeMember);
  if (removeMemberResult.success) {
    yield put(apiActions.business.subscription(authToken, action.payload.businessId))
    yield put(apiActions.business.getInactiveUsers(authToken, action.payload.businessId))
  }
}

export function* setProgressGoals(action) {
  const authToken = yield select(authTokenSelector);
  yield put(apiActions.business.setProgressGoals(authToken, action.payload.businessId, action.payload.goals));
  const result = yield apiActionResult(actions.business.api.setProgressGoals);
  if (result.success) {
    yield put(apiActions.business.profile(authToken, action.payload.businessId))
  }
}

export function* profile(action) {
  const authToken = yield select(authTokenSelector);
  yield put(apiActions.business.profile(authToken, action.payload.businessId));
}

export function* handleBusinessResource(action) {
  const authToken = yield select(authTokenSelector);
  if (authToken.idToken) {
    switch (action.type) {
      case actions.business.getSubscription().type:
        yield put(apiActions.business.subscription(authToken, action.payload.businessId));
        break;
      case actions.business.sendInvitations().type:
        yield put(apiActions.business.sendInvitations(authToken, action.payload.invitationData.businessId, action.payload.invitationData.invitations));
        break;
      case actions.business.getInvitations().type:
        yield put(apiActions.business.invitations(authToken, action.payload.businessId));
        break;
      case actions.business.updateSubscription().type:
        yield put(apiActions.business.updateSubscription(authToken, action.payload.subscriptionData.businessId, action.payload.subscriptionData.subscription));
        break;
      case actions.business.getBusinesses().type:
        yield put(apiActions.business.businesses(authToken));
        break;
      case actions.business.getAfsLicences().type:
        yield put(apiActions.business.getAfsLicences(authToken, action.payload.businessId));
        break;
      case actions.business.getTeamMembers().type:
        yield put(apiActions.business.teamMembers(authToken, action.payload.businessId));
        break;
      case actions.business.queryTeamMembers().type:
        yield put(apiActions.business.queryTeamMembers(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.queryTeamMemberTrainingPlans().type:
        yield put(apiActions.business.queryTeamMemberTrainingPlans(authToken, action.payload.businessId));
        break;
      case actions.business.loadMoreTeamMembers().type:
        yield put(apiActions.business.loadMoreTeamMembers(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.getUnassignedTeamMembers().type:
        yield put(apiActions.business.unassignedTeamMembers(authToken, action.payload.businessId));
        break;
      case actions.business.getFullTeamCpdCategorys().type:
        yield put(apiActions.business.fullTeamCpdCategorys(authToken, action.payload.businessId));
        break;
      case actions.business.getFullTeamPendingExternalCpd().type:
        yield put(apiActions.business.fullTeamPendingExternalCpd(authToken, action.payload.businessId));
        break;
      case actions.business.getSpecificTeamPendingExternalCpd().type:
        yield put(apiActions.business.specificTeamPendingExternalCpd(authToken, action.payload.businessId, action.payload.teamId));
        break;
      case actions.business.updateBusiness().type:
        const { business: { businessDetails, businessImage } } = action.payload;
        const businessFormData = {
          data: businessDetails,
          file: businessImage
        }
        yield put(apiActions.business.updateBusiness(authToken, businessDetails.id, businessFormData));
        break;
      case actions.business.removeAfsl().type:
        yield put(apiActions.business.removeAfsLicence(authToken, action.payload.businessId, action.payload.afslNumber));
        break;
      case actions.business.cancelSubscription().type:
        yield put(apiActions.business.cancelSubscription(authToken, action.payload.businessId));
        break;
      case actions.business.updatePaymentDetails().type:
        yield put(apiActions.business.updatePaymentDetails(authToken, action.payload.businessId, action.payload));
        break;
      case actions.business.verifyInvitation().type:
        yield put(apiActions.business.verifyInvitation({ token: action.payload.token, businessId: action.payload.businessId }));
        break;
      case actions.business.addBusinessAdmin().type:
        yield put(apiActions.business.addBusinessAdmin(authToken, action.payload.businessId, action.payload.memberData));
        const addBusinessAdminResult = yield apiActionResult(actions.business.api.addBusinessAdmin);
        if (addBusinessAdminResult.success) {
          yield put(apiActions.business.teamMembers(authToken, action.payload.businessId));
        }
        break;
      case actions.business.removeBusinessAdmin().type:
        yield put(apiActions.business.removeBusinessAdmin(authToken, action.payload.businessId, action.payload.memberData));
        const removeBusinessAdminResult = yield apiActionResult(actions.business.api.removeBusinessAdmin);
        if (removeBusinessAdminResult.success) {
          yield put(apiActions.business.teamMembers(authToken, action.payload.businessId));
        }
        break;
      case actions.business.getInactiveUsers().type:
        yield put(apiActions.business.getInactiveUsers(authToken, action.payload.businessId))
        break;
      case actions.business.getInactiveUserCpdActivity().type:
        yield put(apiActions.business.getInactiveUserCpdActivity(authToken, action.payload.businessId, action.payload.userId))
        break;
      case actions.business.resendInvitation().type:
        yield put(apiActions.business.resendInvitation(authToken, action.payload.businessId, action.payload.invitationId));
        break;
      case actions.business.deleteInvitation().type:
        yield put(apiActions.business.deleteInvitation(authToken, action.payload.businessId, action.payload.invitationId));
        break;
      case actions.business.getInvitationLink().type:
        yield put(apiActions.business.getInvitationLink(authToken, action.payload.businessId));
        break;
      case actions.business.getIndustryRoles().type:
        yield put(apiActions.business.industryRoles(authToken, action.payload.businessId));
        break;
      case actions.business.addIndustryRole().type:
        yield put(apiActions.business.addIndustryRole(authToken, action.payload.industryUser));
        break;
      case actions.business.getCpdCategorys().type:
        yield put(apiActions.business.getCpdCategorys(authToken, action.payload.businessId));
        break;
      case actions.business.getBusinessActivities().type:
        yield put(apiActions.business.getBusinessActivities(authToken, action.payload.businessId, action.payload.yearsOffset, action.payload.filter));
        break;
      case actions.business.setCpdCategorys().type: {
        const {
          applyChangesToUser,
          applyToAllMembers,
          cpdCategorys,
          businessId,
          industry,
          industryId,
          userId,
          noRedirect
        } = action.payload;

        yield put(actions.business.updateTaskStack(businessId, TaskStatus.updating))

        yield put(apiActions.business.updateIndustryRole(authToken, businessId, {
          industryBusiness: industry,
          applyToAll: applyToAllMembers ? true : false
        }));

        const updateIndustryRoleResult = yield apiActionResult(actions.business.api.updateIndustryRole);

        if (updateIndustryRoleResult.success) {
          yield put(apiActions.business.setCpdCategorys(authToken, businessId, {
            applyToAll: applyToAllMembers ? true : false,
            industryId: industryId,
            pendingCpdCategorys: cpdCategorys
          }));
        }

        const setCpdCategorysResult = yield apiActionResult(actions.business.api.setCpdCategorys);

        if (applyChangesToUser) {

          yield put(userApiActions.user.industryRoles(authToken));
          const getUserIndustryRoles = yield apiActionResult(userActions.user.api.getIndustryRoles);

          if (getUserIndustryRoles.success) {
            const { id: userId } = yield select(currentUserSelector);
            const { id: userIndustryId } = fp.head(yield select(({ user }) => user.currentUserIndustryRoles));

            const mapToUserId = fp.flow(
              fp.mapKeys((key) => key === 'businessId' ? 'userId' : key),
              fp.set('userId')(userId)
            );

            yield put(userActions.user.setCpdCategorys({
              cpdCategorys: fp.map(mapToUserId)(cpdCategorys),
              industry: {
                ...mapToUserId(industry),
                id: userIndustryId
              },
              industryId,
              noRedirect: true,
              userId
            }));
          }
        }

        if (setCpdCategorysResult.success && updateIndustryRoleResult.success) {
          if (applyToAllMembers) {
            yield put(userActions.user.getIndustryRoles(userId));
            yield put(userActions.user.getCpdCategorys(userId));
            yield put(actions.business.setApplyToAllSuccess());
          }
          yield put(actions.business.updateTaskStack(businessId, TaskStatus.completed, {
            ...updateIndustryRoleResult.success.payload,
            trainingPlan: setCpdCategorysResult.success.payload.businessCpdCategorys
          }))
          if (!noRedirect) {
            yield put(push({ pathname: routes.onboarding.teamInvite }));
          }
        }

        break;
      }
      case actions.business.setIndustryRole().type: {
        const {
          industry,
        } = action.payload;

        yield put(apiActions.business.updateIndustryRole(authToken, industry.businessId, {
          industryBusiness: industry,
          applyToAll: false
        }));

        break;
      }
      case actions.business.setTrainingPlan().type: {
        const {
          cpdCategorys,
          industry,
          industryId
        } = action.payload;

        yield put(apiActions.business.setCpdCategorys(authToken, industry.businessId, {
          applyToAll: false,
          industryId: industryId,
          pendingCpdCategorys: cpdCategorys
        }));

        break;
      }
      default:
        console.error(`Unknown authenticated action received [${action.type}].`);
    }
  }
  else {
    switch (action.type) {
      case actions.business.verifyInvitation().type:
        yield put(apiActions.business.verifyInvitation({ token: action.payload.token, businessId: action.payload.businessId }));
        break;
      default:
        console.error(`Unknown common action received [${action.type}].`);
    }
  }
}

export const sagas = all([
  takeLatest(actions.business.subscribePlan().type, subscribePlan),
  takeLatest(actions.business.addAfsLicence().type, addAfsl),
  takeLatest(actions.business.addBusinessManager().type, addBusinessManager),
  takeLatest(actions.business.removeBusinessManager().type, removeBusinessManager),
  takeLatest(actions.business.removeMember().type, removeBusinessMember),
  takeLatest(actions.business.setProgressGoals().type, setProgressGoals),
  takeLatest(actions.business.profile().type, profile),
  takeLatest([
    actions.business.getSubscription().type,
    actions.business.sendInvitations().type,
    actions.business.getInvitations().type,
    actions.business.updateSubscription().type,
    actions.business.getBusinesses().type,
    actions.business.getAfsLicences().type,
    actions.business.getTeamMembers().type,
    actions.business.queryTeamMembers().type,
    actions.business.queryTeamMemberTrainingPlans().type,
    actions.business.loadMoreTeamMembers().type,
    actions.business.getUnassignedTeamMembers().type,
    actions.business.getFullTeamCpdCategorys().type,
    actions.business.getFullTeamPendingExternalCpd().type,
    actions.business.getSpecificTeamPendingExternalCpd().type,
    actions.business.updateBusiness().type,
    actions.business.verifyInvitation().type,
    actions.business.removeAfsl().type,
    actions.business.cancelSubscription().type,
    actions.business.updatePaymentDetails().type,
    actions.business.getInactiveUsers().type,
    actions.business.getInactiveUserCpdActivity().type,
    actions.business.addBusinessAdmin().type,
    actions.business.removeBusinessAdmin().type,
    actions.business.resendInvitation().type,
    actions.business.deleteInvitation().type,
    actions.business.getIndustryRoles().type,
    actions.business.addIndustryRole().type,
    actions.business.getCpdCategorys().type,
    actions.business.setCpdCategorys().type,
    actions.business.setTrainingPlan().type,
    actions.business.setIndustryRole().type,
    actions.business.getInvitationLink().type,
    actions.business.getBusinessActivities().type,
  ], handleBusinessResource)
])

function* businessSagas() {
  yield sagas;
}

export default businessSagas;


// WEBPACK FOOTER //
// ./src/state/modules/business/business/sagas.js